/**
 * VideoLibraryPage.tsx
 *
 * The account-side Video Library — the video twin of PhotoLibraryPage.
 *
 * Same shape and same rules: this account's own videos plus the ones the master
 * account shares, folder filtering, a shared/account scope filter, multi-select
 * with bulk move and delete, upload with drag-drop and a progress bar, and a
 * detail view with a copyable URL and folder reassignment.
 *
 * Two differences from the photo page, both following from the medium:
 *  - tiles are <video preload="metadata"> rather than <img>, so the first frame
 *    is the thumbnail and the detail modal plays the file with controls;
 *  - uploads are size-capped (500 MB) and validated before the transfer starts.
 *
 * Shared videos are read-only here exactly as shared photos are: they belong to
 * the master account and are managed in the corporate dashboard.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, Search, FolderOpen, Download, Trash2,
  Video as VideoIcon, X, Copy, Check, ChevronDown, Play,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { usePageContext } from '@/hooks/usePageContext';
import { apiClient } from '@/services/apiClient';
import {
  uploadMarketingVideo,
  getVideoFolders,
  deleteMarketingVideo,
  moveMarketingVideos,
  formatDuration,
  isAllowedVideoType,
  MAX_VIDEO_BYTES,
  MAX_VIDEO_LABEL,
  VIDEO_ACCEPT_ATTR,
  VIDEO_FORMAT_LABEL,
} from '@/services/marketing.service';
import { FolderSelect } from './FolderSelect';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface Video {
  id: number;
  accountID: number;
  masterAccountID?: number;
  /**
   * True when the video belongs to the master account and every sibling account
   * sees it. Shared videos are uploaded and managed in the corporate dashboard,
   * so this page shows them read-only: no move, no delete.
   */
  shared?: boolean;
  url: string;
  originalFilename: string;
  altText: string | null;
  folder: string | null;
  folderID?: number;
  fileSize: number;
  mimeType?: string;
  duration?: number;
  width?: number;
  height?: number;
  createDate: string;
  searchTerms: string;
  attachedTo: string[];
}

type ScopeFilter = 'all' | 'mine' | 'shared';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/* ------------------------------------------------------------------ */
/*  Scopes                                                             */
/* ------------------------------------------------------------------ */
/**
 * The campaign Video Library under Marketing -> Content Bank. Kept as a scope table
 * so the endpoints and size ceiling stay together in one place.
 */
export type VideoLibraryScope = 'marketing';

const SCOPES = {
  marketing: {
    title: 'Video Library',
    endpoint: '/marketing/videos',
    listKey: ['marketing', 'videos'] as const,
    foldersKey: ['marketing', 'video-folders'] as const,
    folderScope: 'videos' as const,
    getFolders: getVideoFolders,
    upload: uploadMarketingVideo,
    remove: deleteMarketingVideo,
    move: moveMarketingVideos,
    maxBytes: MAX_VIDEO_BYTES,
    maxLabel: MAX_VIDEO_LABEL,
  },
} as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function VideoLibraryPage({ scope = 'marketing' }: { scope?: VideoLibraryScope } = {}) {
  const cfg = SCOPES[scope];
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const updateAIContext = usePageContext(cfg.title);

  /* -- filter / search state ---------------------------------------- */
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  /* -- selection state ---------------------------------------------- */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setMoveDropdownOpen(false);
  };

  /* -- upload modal state ------------------------------------------- */
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadAlt, setUploadAlt] = useState('');
  const [uploadFolder, setUploadFolder] = useState('general');
  const [uploadProgress, setUploadProgress] = useState<{ pct: number; loaded: number; total: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Uploading from the library page is an explicit "keep this" action, unlike
  // the campaign pickers where the box defaults off.
  const SAVE_TO_LIBRARY = true;

  /* -- detail modal state ------------------------------------------- */
  const [detailVideo, setDetailVideo] = useState<Video | null>(null);
  const [detailFolder, setDetailFolder] = useState('general');
  const [copiedUrl, setCopiedUrl] = useState(false);

  /* ================================================================ */
  /*  Queries                                                         */
  /* ================================================================ */
  const { data: videos, isLoading } = useQuery({
    queryKey: cfg.listKey,
    queryFn: async () => {
      // No all_accounts flag: the API returns this account's own videos plus the
      // ones the master account shares, which is the whole visible set.
      const res = await apiClient.get(cfg.endpoint);
      return res.data.data as Video[];
    },
  });

  // Shares its key with FolderSelect's 'videos' scope, so both read the same
  // shape — the service getter normalises the endpoint's `folder` field to
  // `name` and carries `scope` / `isSystem` through.
  const { data: folders } = useQuery({
    queryKey: cfg.foldersKey,
    queryFn: () => cfg.getFolders(),
  });

  // Counting off the loaded videos rather than trusting the endpoint's count
  // keeps the selector honest about what clicking a folder will actually show,
  // since the scope filter narrows the grid client-side.
  const folderRows = useMemo(
    () =>
      (folders ?? []).map((f) => ({
        ...f,
        count: (videos ?? []).filter((v) => (v.folder || 'general') === f.name).length,
      })),
    [folders, videos],
  );

  const q = search.trim().toLowerCase();
  const filteredVideos = useMemo(() => {
    let result = videos ?? [];
    if (folder) result = result.filter((v) => v.folder === folder);
    if (scopeFilter === 'shared') result = result.filter((v) => v.shared);
    else if (scopeFilter === 'mine') result = result.filter((v) => !v.shared);
    if (q) {
      result = result.filter((v) => {
        const haystack = `${v.originalFilename ?? ''} ${v.altText ?? ''} ${v.folder ?? ''} ${v.searchTerms ?? ''}`.toLowerCase();
        return haystack.includes(q);
      });
    }
    return result;
  }, [videos, folder, scopeFilter, q]);

  /** Shared videos are the master account's; this page can only file its own. */
  const selectedMovableIds = useMemo(
    () =>
      Array.from(selectedIds).filter(
        (id) => !(videos ?? []).find((v) => v.id === id)?.shared,
      ),
    [selectedIds, videos],
  );

  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || '(none)'}, Folder: ${folder || 'All'}`,
      dataSummary: videos
        ? `${videos.length} video(s) displayed, ${folders?.length ?? 0} folder(s)`
        : 'Loading...',
    });
  }, [search, folder, videos, folders, updateAIContext]);

  /* ================================================================ */
  /*  Mutations                                                       */
  /* ================================================================ */
  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('No file');
      return cfg.upload(uploadFile, {
        altText: uploadAlt,
        folder: uploadFolder,
        saveToLibrary: SAVE_TO_LIBRARY,
        onProgress: (pct, loaded, total) => setUploadProgress({ pct, loaded, total }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      closeUploadModal();
      toast.success('Video uploaded');
    },
    onError: (err: unknown) => {
      setUploadProgress(null);
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(msg ?? 'Upload failed');
    },
  });

  const moveMut = useMutation({
    mutationFn: async ({ ids, targetFolder }: { ids: number[]; targetFolder: string }) => {
      await cfg.move(ids, targetFolder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      clearSelection();
      toast.success('Videos moved');
    },
    onError: () => toast.error('Move failed'),
  });

  /**
   * Soft delete — the API flips active to 0, so the row and the S3 object both
   * survive. Only the account's own videos can go: the API returns 422 for a
   * master-shared video, and its message says so, so it is surfaced rather than
   * swallowed. There is no in-use check, unlike photos — nothing attaches a
   * video to a product, facility, membership or appointment type.
   */
  const deleteMut = useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => cfg.remove(id)));
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      return { deleted: ids.length - failures.length, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      clearSelection();
      setDetailVideo(null);
      if (deleted > 0) toast.success(`${deleted} video${deleted === 1 ? '' : 's'} deleted`);
      if (failures.length > 0) {
        const res = (failures[0].reason as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        toast.error(res ?? `${failures.length} video(s) could not be deleted`);
      }
    },
    onError: () => toast.error('Delete failed'),
  });

  const moveDetailMut = useMutation({
    mutationFn: async ({ id, targetFolder }: { id: number; targetFolder: string }) => {
      await cfg.move([id], targetFolder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      setDetailVideo(null);
      toast.success('Video moved');
    },
    onError: () => toast.error('Move failed'),
  });

  /* ================================================================ */
  /*  Handlers                                                        */
  /* ================================================================ */
  const handleFileSelect = (files: FileList | null) => {
    if (!files?.length) return;
    const file = files[0];
    if (!isAllowedVideoType(file)) {
      toast.error(`Please select a video file (${VIDEO_FORMAT_LABEL})`);
      return;
    }
    // Checked before the transfer starts, so an oversized file costs nothing.
    if (file.size > cfg.maxBytes) {
      toast.error(`"${file.name}" is ${formatBytes(file.size)}. Maximum size is ${cfg.maxLabel}.`);
      return;
    }
    setUploadFile(file);
    // An object URL, not a data URL: a 100 MB base64 string would be ~133 MB of
    // string in memory just to render a preview.
    setUploadPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    if (!uploadOpen) setUploadOpen(true);
  };

  const closeUploadModal = () => {
    setUploadOpen(false);
    setUploadFile(null);
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadPreview('');
    setUploadAlt('');
    setUploadFolder('general');
    setUploadProgress(null);
    setDragOver(false);
  };

  // Release the preview URL if the component goes away mid-upload.
  useEffect(() => () => { if (uploadPreview) URL.revokeObjectURL(uploadPreview); }, [uploadPreview]);

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
    toast.success('URL copied to clipboard');
  };

  const downloadVideo = (video: Video) => {
    const a = document.createElement('a');
    a.href = video.url;
    a.download = video.originalFilename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onVideoCardClick = (video: Video) => {
    if (selectedIds.size > 0) {
      toggleSelect(video.id);
    } else {
      setDetailVideo(video);
      setDetailFolder(video.folder ?? 'general');
    }
  };

  /* Close move dropdown on outside click */
  useEffect(() => {
    if (!moveDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.move-dropdown-container')) {
        setMoveDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [moveDropdownOpen]);

  /* Drag-and-drop helpers for the upload dropzone */
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  /* ================================================================ */
  /*  Render                                                          */
  /* ================================================================ */
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-5 pt-5 pb-4 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <FolderSelect
            scope={cfg.folderScope}
            folders={folderRows}
            activeFolder={folder}
            onSelect={(f) => { setFolder(f); clearSelection(); }}
            totalCount={videos?.length ?? 0}
          />

          {/* Shared / account scope filter */}
          <div className="flex items-center rounded-md border border-slate-200 overflow-hidden">
            {([
              ['all', 'All'],
              ['mine', 'This Account'],
              ['shared', 'Shared'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => { setScopeFilter(value); clearSelection(); }}
                className={cn(
                  'px-3 py-2 text-sm transition-colors',
                  scopeFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-[220px] justify-end">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search videos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button leftIcon={<Upload className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
          </div>
        </div>

        {/* Bulk action bar */}
        {hasSelection && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500">{selectedIds.size} selected</span>
            <Button variant="ghost" size="sm" onClick={clearSelection}>Deselect all</Button>

            <div className="relative move-dropdown-container">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FolderOpen className="h-4 w-4" />}
                rightIcon={<ChevronDown className="h-3 w-3" />}
                disabled={selectedMovableIds.length === 0 || moveMut.isPending}
                title={
                  selectedMovableIds.length === 0
                    ? 'Shared videos are filed in the corporate dashboard'
                    : undefined
                }
                onClick={() => setMoveDropdownOpen((o) => !o)}
              >
                Move
              </Button>
              {moveDropdownOpen && (
                <div className="absolute z-20 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg py-1">
                  {(folders ?? []).map((f) => (
                    <button
                      key={f.name}
                      type="button"
                      className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                      onClick={() => {
                        moveMut.mutate({ ids: selectedMovableIds, targetFolder: f.name });
                      }}
                    >
                      {f.name.charAt(0).toUpperCase() + f.name.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Same ownership rule as Move: a shared video is the master's. */}
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Trash2 className="h-4 w-4" />}
              disabled={selectedMovableIds.length === 0 || deleteMut.isPending}
              title={
                selectedMovableIds.length === 0
                  ? 'Shared videos are removed in the corporate dashboard'
                  : undefined
              }
              onClick={() => {
                if (confirm(`Delete ${selectedMovableIds.length} video(s)?`)) {
                  deleteMut.mutate(selectedMovableIds);
                }
              }}
            >
              Delete
            </Button>

            {selectedIds.size - selectedMovableIds.length > 0 && (
              <span className="text-xs text-slate-400">
                {selectedIds.size - selectedMovableIds.length} shared video
                {selectedIds.size - selectedMovableIds.length === 1 ? '' : 's'} skipped
              </span>
            )}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {isLoading ? (
          <div className="py-20 text-center text-slate-400 text-sm">Loading videos…</div>
        ) : filteredVideos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <VideoIcon className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">{q ? 'No videos match your search' : 'No videos yet'}</h3>
            {!q && <p className="text-sm text-slate-500 mt-1 mb-4">Upload videos to use in your campaigns and social posts.</p>}
            {!q && (
              <Button leftIcon={<Upload className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>
                Upload First Video
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
            {filteredVideos.map((video) => {
              const isSelected = selectedIds.has(video.id);
              const duration = formatDuration(video.duration);
              return (
                <div
                  key={video.id}
                  className={cn(
                    'relative group rounded-lg overflow-hidden border-2 aspect-square cursor-pointer transition-all bg-slate-900',
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-slate-200 hover:border-slate-300',
                  )}
                  onClick={() => onVideoCardClick(video)}
                >
                  {/*
                    preload="metadata" is what makes the first frame the tile: the
                    browser fetches enough of the file to render a poster and stops.
                  */}
                  <video
                    src={video.url}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    muted
                    playsInline
                    tabIndex={-1}
                  />

                  {/* A bare first frame reads as a broken image without this. */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/45 rounded-full p-2.5 group-hover:bg-black/60 transition-colors">
                      <Play className="h-5 w-5 text-white" fill="white" />
                    </div>
                  </div>

                  {duration && (
                    <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded pointer-events-none">
                      {duration}
                    </span>
                  )}

                  {/* Always-on badge: a shared video behaves differently (read-only here). */}
                  {video.shared && (
                    <span
                      className="absolute top-1.5 right-1.5 bg-primary/90 text-primary-foreground text-[9px] font-semibold px-1.5 py-0.5 rounded pointer-events-none"
                      title="Shared across the master account — managed in the corporate dashboard"
                    >
                      Shared
                    </span>
                  )}

                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <p className="text-[11px] text-white truncate">{video.originalFilename}</p>
                    <p className="text-[10px] text-white/70">{video.folder}</p>
                  </div>

                  <div
                    className={cn(
                      'absolute top-1.5 left-1.5 transition-opacity',
                      isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                    )}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(video.id); }}
                  >
                    <div className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                      isSelected
                        ? 'bg-primary border-primary'
                        : 'bg-white/80 border-slate-400 hover:border-primary',
                    )}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Upload modal ────────────────────────────────────────────── */}
      <Modal isOpen={uploadOpen} onClose={closeUploadModal} title="Upload Video" size="md">
        <div className="space-y-4">
          <FormField label="Folder">
            <select
              value={uploadFolder}
              onChange={(e) => setUploadFolder(e.target.value)}
              disabled={uploadMut.isPending}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {(folders ?? []).map((f) => (
                <option key={f.name} value={f.name}>
                  {f.name.charAt(0).toUpperCase() + f.name.slice(1)}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Description (optional)">
            <Input
              value={uploadAlt}
              onChange={(e) => setUploadAlt(e.target.value)}
              placeholder="What is in this video?"
              disabled={uploadMut.isPending}
            />
          </FormField>

          {uploadProgress !== null ? (
            <div className="py-6 space-y-3">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.pct}%` }} />
              </div>
              {/* At 100% the bytes are in storage and the server is filing the video. */}
              <p className="text-sm text-center text-slate-500">
                {uploadProgress.pct >= 100
                  ? 'Finishing up…'
                  : `Uploading… ${uploadProgress.pct}% · ${formatBytes(uploadProgress.loaded)} of ${formatBytes(uploadProgress.total)}`}
              </p>
            </div>
          ) : uploadFile && uploadPreview ? (
            <div className="flex flex-col items-center gap-2">
              <div className="relative w-full max-w-[320px] rounded-lg overflow-hidden border border-slate-200 bg-black">
                <video src={uploadPreview} className="w-full max-h-[200px] object-contain" controls preload="metadata" playsInline />
                <button
                  type="button"
                  onClick={() => { setUploadFile(null); if (uploadPreview) URL.revokeObjectURL(uploadPreview); setUploadPreview(''); }}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-1 z-10"
                >
                  <X className="h-4 w-4 text-white" />
                </button>
              </div>
              <p className="text-sm text-slate-600 truncate max-w-[280px]">{uploadFile.name}</p>
              <p className="text-xs text-slate-400">{formatBytes(uploadFile.size)}</p>
            </div>
          ) : (
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={cn(
                'flex flex-col items-center justify-center h-[180px] border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                dragOver ? 'border-primary bg-primary/5' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
              )}
            >
              <Upload className="h-9 w-9 text-slate-400 mb-2" />
              <p className="text-sm font-medium text-slate-600">Click to select or drag and drop</p>
              <p className="text-xs text-slate-400 mt-1">{VIDEO_FORMAT_LABEL} up to {cfg.maxLabel}</p>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept={VIDEO_ACCEPT_ATTR}
            className="hidden"
            onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeUploadModal} disabled={uploadMut.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => uploadMut.mutate()}
              disabled={!uploadFile || uploadMut.isPending}
            >
              Upload
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Detail modal ────────────────────────────────────────────── */}
      <Modal
        isOpen={!!detailVideo}
        onClose={() => setDetailVideo(null)}
        title={detailVideo?.originalFilename ?? 'Video'}
        size="xl"
      >
        {detailVideo && (
          <div className="space-y-4">
            <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center max-h-[420px]">
              {/*
                key={url} forces a fresh element when the modal moves to another
                video, so the player does not keep the previous source's state.
              */}
              <video
                key={detailVideo.url}
                src={detailVideo.url}
                className="max-w-full max-h-[420px]"
                controls
                autoPlay
                preload="metadata"
                playsInline
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="block text-xs font-medium text-slate-500">Folder</span>
                <span className="text-slate-800">{detailVideo.folder ?? 'general'}</span>
              </div>
              {formatDuration(detailVideo.duration) && (
                <div>
                  <span className="block text-xs font-medium text-slate-500">Duration</span>
                  <span className="text-slate-800">{formatDuration(detailVideo.duration)}</span>
                </div>
              )}
              <div>
                <span className="block text-xs font-medium text-slate-500">File size</span>
                <span className="text-slate-800">
                  {detailVideo.fileSize > 0 ? formatBytes(detailVideo.fileSize) : 'Unknown'}
                </span>
              </div>
              {(detailVideo.width ?? 0) > 0 && (
                <div>
                  <span className="block text-xs font-medium text-slate-500">Resolution</span>
                  <span className="text-slate-800">{detailVideo.width} × {detailVideo.height} px</span>
                </div>
              )}
              {detailVideo.createDate && (
                <div>
                  <span className="block text-xs font-medium text-slate-500">Added</span>
                  <span className="text-slate-800">
                    {new Date(detailVideo.createDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            <div>
              <span className="block text-xs font-medium text-slate-500 mb-1">URL</span>
              <div className="flex items-center gap-2">
                <Input value={detailVideo.url} readOnly className="text-xs" />
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={copiedUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  onClick={() => copyUrl(detailVideo.url)}
                >
                  {copiedUrl ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            {detailVideo.shared ? (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                This video is shared across the master account. Move or remove it in the
                corporate dashboard.
              </p>
            ) : (
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <span className="block text-xs font-medium text-slate-500 mb-1">Move to folder</span>
                  <select
                    value={detailFolder}
                    onChange={(e) => setDetailFolder(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  >
                    {(folders ?? []).map((f) => (
                      <option key={f.name} value={f.name}>
                        {f.name.charAt(0).toUpperCase() + f.name.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="secondary"
                  leftIcon={<FolderOpen className="h-4 w-4" />}
                  disabled={detailFolder === (detailVideo.folder ?? 'general') || moveDetailMut.isPending}
                  onClick={() => moveDetailMut.mutate({ id: detailVideo.id, targetFolder: detailFolder })}
                >
                  Move
                </Button>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2 border-t border-slate-200">
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={() => downloadVideo(detailVideo)}
              >
                Download
              </Button>
              {!detailVideo.shared && (
                <Button
                  variant="danger"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  disabled={deleteMut.isPending}
                  onClick={() => {
                    if (confirm('Delete this video?')) deleteMut.mutate([detailVideo.id]);
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
